import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, message, Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import LoadingSpinner from '../../../components/loadingSpinner'
import { useApolloClient, useQuery } from '@apollo/react-hooks'
import type {
	LicenseContractsQuery,
	LicenseContractsGroupType,
	LicenseContractType,
	GeneralLicenseFacilityType
} from '../../../generated/types'
import { LICENSE_CONTRACTS } from '../../core/queries'
import { GeneralFacilityRow } from '../components/GeneralFacilityRow'
import {
	StyledContent,
	StyledPageTitle,
	StyledSystemSection,
	StyledCenteredContainer,
	StyledDocumentsLabel,
	StyledDocumentRow
} from './styles'
import {
	StyledCard,
	StyledCardTitle,
	StyledCardSubtitle
} from '../components/styles'
import { downloadFile, slugify } from '../../../utils/download.utils'
import { handleError } from '../../../utils/error.utils'
import { safeOpen } from '../../../utils/helper.utils'
import type { Previewable } from './types'

const { Title, Text, Paragraph } = Typography

const UPDATED_MARKER_STYLE: React.CSSProperties = {
	color: '#b8860b',
	fontWeight: 600,
	marginLeft: 8
}

export const LicenseContractsPage = () => {
	const { t } = useTranslation()
	const client = useApolloClient()
	const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())
	const [resolvedUpdatedDocumentIds, setResolvedUpdatedDocumentIds] = useState<
		Set<string>
	>(new Set())

	// Use GraphQL query
	const { data, loading, error, refetch } = useQuery<LicenseContractsQuery>(
		LICENSE_CONTRACTS,
		{
			fetchPolicy: 'network-only'
		}
	)
	const allContractsZipUrl = data?.allContractsZip || null

	// Handle error state
	useEffect(() => {
		if (error) {
			message.error(t('license.contracts.error'))
			console.error('Error loading license contracts:', error)
		}
	}, [error, t])

	// Get license contracts and validate data
	const rawLicenseContractGroups =
		data?.licenseContracts?.filter(
			(group): group is LicenseContractsGroupType => group !== null
		) || []

	// Filter and validate: only include systems with valid data
	const licenseContracts = rawLicenseContractGroups
		.map(group => {
			// Validate system has required data
			if (!group.softwareSystem) {
				console.warn('System has no software system data, skipping:', group)
				return null
			}

			// Filter out contracts with incomplete data
			const validContracts =
				group.contracts
					?.filter(
						(contract): contract is LicenseContractType => contract !== null
					)
					?.filter(contract => {
						const isValid =
							contract.id &&
							contract.status &&
							contract.licenseVersion?.version &&
							contract.licenseVersion?.licenseMaster?.name

						if (!isValid) {
							console.warn('Invalid contract data detected:', {
								contractId: contract.id,
								status: contract.status,
								version: contract.licenseVersion?.version,
								licenseName: contract.licenseVersion?.licenseMaster?.name
							})
						}
						return isValid
					}) || []

			// Only include system if it has at least one valid contract
			if (validContracts.length === 0) {
				const systemIdentifier = group.softwareSystem?.systemName || 'Unknown'
				console.warn(
					'System has no valid contracts, skipping:',
					systemIdentifier
				)
				return null
			}

			return {
				...group,
				contracts: validContracts
			} as LicenseContractsGroupType & {
				contracts: LicenseContractType[]
			}
		})
		.filter(
			(
				group
			): group is NonNullable<
				LicenseContractsGroupType & {
					contracts: LicenseContractType[]
				}
			> => group !== null
		)
	// Extract and sanitize the list of general license facilities returned by
	// the query. These are separate from the per-system contracts and are
	// displayed in their own section with preview buttons.
	const generalFacilities: GeneralLicenseFacilityType[] =
		data?.allGeneralLicenseFacilities?.filter(
			(f): f is GeneralLicenseFacilityType => f !== null
		) ?? []

	const handleDownload = async (
		downloadUrl: string,
		docId: string,
		filename: string,
		isZip = false
	) => {
		setDownloadingIds(prev => new Set(prev).add(docId))
		try {
			const clean_filename = filename.replace(/\.[a-z0-9]+$/i, '')
			await downloadFile(downloadUrl, {
				fallbackFileName: isZip
					? `${slugify(clean_filename)}.zip`
					: `${slugify(clean_filename)}.pdf`
			})
			message.success(t('license.contracts.downloadSuccess'))
		} catch (error) {
			handleError(error)
			message.error(t('license.download.error'))
		} finally {
			setDownloadingIds(prev => {
				const next = new Set(prev)
				next.delete(docId)
				return next
			})
		}
	}

	// Handle loading state
	if (loading) {
		return (
			<StyledContent>
				<StyledCenteredContainer>
					<LoadingSpinner />
				</StyledCenteredContainer>
			</StyledContent>
		)
	}

	// Convert a GraphQL facility object into the generic Previewable
	// shape expected by `openPreview`. This keeps the preview logic
	// decoupled from the exact shape of the data model.
	const asPreviewableFromGeneralFacility = (
		f: GeneralLicenseFacilityType
	): Previewable => ({
		kind: 'generalFacility',
		title: f.title,
		pdfUrl: f.pdfUrl,
		htmUrl: f.htmUrl
	})

	const openPreview = (item: Previewable) => {
		if (item.pdfUrl) {
			safeOpen(item.pdfUrl)
			return
		}

		if (item.kind === 'generalFacility' && item.htmUrl) {
			safeOpen(item.htmUrl)
			return
		}
	}

	const getUpdatedTextUrl = (documentId: string, downloadUrl: string) => {
		const base = new URL(downloadUrl, window.location.origin)
		return `${base.origin}/license/documents/${documentId}/updated-text/`
	}

	const openUpdatedText = async (documentId: string, downloadUrl: string) => {
		try {
			const updatedUrl = getUpdatedTextUrl(documentId, downloadUrl)
			const newWindow = window.open(updatedUrl, '_blank', 'noopener,noreferrer')
			if (!newWindow) {
				console.warn(
					'Popup handle is not available for the updated document. Continuing with local refresh.'
				)
			}

			setResolvedUpdatedDocumentIds(prev => new Set(prev).add(documentId))

			window.setTimeout(async () => {
				try {
					await client.clearStore()
					await refetch()
				} catch (refreshError) {
					console.warn(
						'Could not refresh contracts after opening updated document URL.',
						refreshError
					)
				}
			}, 1000)
		} catch (error) {
			console.warn('Could not refresh and open updated document URL.', error)
			console.warn(
				'Updated document was not opened because the refresh/open sequence failed.'
			)
		}
	}

	// Handle error state (message displayed in useEffect above)
	if (error) {
		return (
			<StyledContent>
				<StyledCenteredContainer>
					<p>{t('license.contracts.error')}</p>
				</StyledCenteredContainer>
			</StyledContent>
		)
	}

	// Handle empty state - no contracts
	if (!licenseContracts || licenseContracts.length === 0) {
		return (
			<StyledContent>
				<StyledCenteredContainer>
					<Title level={3}>{t('license.contracts.empty')}</Title>
				</StyledCenteredContainer>
			</StyledContent>
		)
	}

	return (
		<StyledContent>
			<StyledPageTitle level={2}>
				{t('license.contracts.title')}
			</StyledPageTitle>
			{licenseContracts.map(system => {
				const systemName =
					system.softwareSystem?.systemName || 'Unbekanntes System'

				return (
					<StyledSystemSection key={system.softwareSystem?.id}>
						<Title level={3}>{systemName}</Title>

						{(system.contracts as LicenseContractType[]).map(contract => {
							const version = contract.licenseVersion?.version || ''
							const licenseName =
								contract.licenseVersion?.licenseMaster?.name || ''
							const licenseDescription =
								contract.licenseVersion?.licenseMaster?.description || ''
							const status = contract.status || ''
							const finishedAt = contract.finishedAt
								? new Date(contract.finishedAt).toLocaleDateString('de-DE')
								: null
							const documents = contract.documents || []

							return (
								<StyledCard
									key={contract.id}
									title={
										<div>
											<StyledCardTitle>{licenseName}</StyledCardTitle>
											<StyledCardSubtitle>
												Version {version} | Status: {status}
												{finishedAt &&
													` | ${t(
														'license.contracts.finishedAt'
													)}: ${finishedAt}`}
											</StyledCardSubtitle>
										</div>
									}
								>
									{licenseDescription && (
										<Paragraph>{licenseDescription}</Paragraph>
									)}
									{documents.length > 0 ? (
										<div>
											<StyledDocumentsLabel strong>
												{t('license.contracts.documents')}:
											</StyledDocumentsLabel>
											{documents
												.filter(
													(doc): doc is NonNullable<typeof doc> => doc !== null
												)
												.map((doc, docIndex) => {
													const filename =
														doc.documentFilename ?? `document_${docIndex + 1}`
													const isOutdated =
														Boolean(doc.isOutdated) &&
														!resolvedUpdatedDocumentIds.has(doc.id)
													const effectiveDownloadUrl =
														resolvedUpdatedDocumentIds.has(doc.id)
															? getUpdatedTextUrl(doc.id, doc.downloadUrl)
															: doc.downloadUrl
													const updatedTextTitle = doc.latestUpdatedText?.title
													return (
														<StyledDocumentRow key={doc.id}>
															<Text>
																{filename}
																{isOutdated && (
																	<span style={UPDATED_MARKER_STYLE}>
																		{t('license.contracts.updatedMarker')}
																	</span>
																)}
															</Text>
															{isOutdated ? (
																<Button
																	type="link"
																	onClick={() =>
																		openUpdatedText(doc.id, doc.downloadUrl)
																	}
																>
																	{updatedTextTitle
																		? t('license.contracts.viewUpdatedText', {
																				title: updatedTextTitle
																			})
																		: t(
																				'license.contracts.viewUpdatedTextFallback'
																			)}
																</Button>
															) : (
																<Button
																	type="link"
																	icon={<DownloadOutlined />}
																	loading={downloadingIds.has(doc.id)}
																	onClick={() =>
																		handleDownload(
																			effectiveDownloadUrl,
																			doc.id,
																			filename,
																			false
																		)
																	}
																>
																	{t('license.contracts.download')}
																</Button>
															)}
														</StyledDocumentRow>
													)
												})}
										</div>
									) : (
										<Text type="secondary">
											{t('license.contracts.noDocuments')}
										</Text>
									)}
								</StyledCard>
							)
						})}
					</StyledSystemSection>
				)
			})}

			{generalFacilities.length > 0 && (
				<StyledSystemSection key="general-facilities">
					<Title level={3}>{t('license.contracts.generalFacilities')}</Title>
					<StyledCard>
						{generalFacilities
							.slice()
							.sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
							.map(f => (
								<GeneralFacilityRow
									key={f.id}
									facility={f}
									onPreview={facility =>
										openPreview(asPreviewableFromGeneralFacility(facility))
									}
								/>
							))}
					</StyledCard>
				</StyledSystemSection>
			)}

			{allContractsZipUrl && (
				<Button
					type="primary"
					icon={<DownloadOutlined />}
					style={{ marginBottom: 24 }}
					loading={downloadingIds.has('all_contracts_zip')}
					onClick={() =>
						handleDownload(
							allContractsZipUrl,
							'all_contracts_zip',
							data?.allContractsZipFilename ?? 'alle_Lizenzvertraege',
							true
						)
					}
				>
					{t('license.contracts.downloadAllContracts')}
				</Button>
			)}
		</StyledContent>
	)
}
